// کدهای هزینه
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa    = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng   = s => String(s).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtNum  = n => n > 0 ? Number(n).toLocaleString('fa-IR') : '•';

const KINDS = ['هزینه‌ای', 'تملک دارایی سرمایه‌ای'];
const EMPTY = {
  sanama_code: 0, code: '', code_ext: 0, code_ext2: 0, code_ext3: 0,
  title: '', accounting_kind: 0, entity_id: 0, entity: '', is_active: true,
};

const API = (path, opts = {}) => fetch(`/api/budget${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const Field = ({ label, value, onChange, type = 'text', half }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: half ? '0 0 48%' : 1 }}>
    <label style={ui.mlabel}>{label}</label>
    <input type={type} value={value} onChange={onChange} style={ui.input} />
  </div>
);

// جستجوی موجودیت هزینه
function EntityField({ value, onPick }) {
  const [q, setQ]       = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const doSearch = async (text) => {
    setQ(text);
    const j = await API(`/hazineh/entity-search/?q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, position: 'relative' }}>
      <label style={ui.mlabel}>موجودیت هزینه</label>
      <input value={value || q} onFocus={() => doSearch(q)} onChange={e => doSearch(e.target.value)}
        placeholder="جستجوی موجودیت..." style={ui.input} />
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 600, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.title}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BudgetHazineh({ user, onBack }) {
  // دکمه‌های فرم FRMHAZINEH؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMHAZINEH', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const curYear = new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  const [year,   setYear]   = useState(curYear);
  const [search, setSearch] = useState('');
  const [kind,   setKind]   = useState('all');
  const [active, setActive] = useState('all');
  const [levels, setLevels] = useState([1, 2, 3, 4, 5, 6]);

  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');

  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [hasMore,  setHasMore]  = useState(false);
  const [loadMore, setLoadMore] = useState(false);

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const initW = [80, 80, 55, 55, 55, 210, 90, 55, 150, 55, 110];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const buildUrl = (yr, pg) => {
    const lv = (levels.length && levels.length < 6) ? `&levels=${levels.join(',')}` : '';
    return `/hazineh/?year=${yr}&search=${encodeURIComponent(search)}&kind=${kind}&active=${active}${lv}&page=${pg}`;
  };

  const load = useCallback(async (yr = year) => {
    setLoading(true); setError(''); setPage(1); setSelected(null);
    const j = await API(buildUrl(yr, 1));
    setLoading(false);
    if (j.ok) { setData(j.data); setTotal(j.total || j.data.length); setHasMore(j.has_more || false); }
    else setError(j.msg);
  }, [year, search, kind, active, levels]);

  const loadNextPage = async () => {
    const next = page + 1; setLoadMore(true);
    const j = await API(buildUrl(year, next));
    setLoadMore(false);
    if (j.ok) { setData(d => [...d, ...j.data]); setHasMore(j.has_more || false); setPage(next); }
  };

  useEffect(() => { load(); }, [kind, active, levels]);

  const handleYear = e => {
    const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')).slice(0, 4);
    setYear(v);
    if (v.length === 4) load(v);
  };

  const toggleLevel = n => setLevels(ls => ls.includes(n) ? ls.filter(x => x !== n) : [...ls, n].sort());

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm(EMPTY); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return; if (!selected) return; setForm({ ...selected }); setModal('edit'); };
  const openDel  = () => { if (!vbf.can('BTNDELETE')) return; if (!selected) return; setModal('delete'); };

  const save = async (force = false) => {
    if (!String(form.code).trim()) { setError('کد هزینه الزامی است'); return; }
    if (!form.title.trim())        { setError('عنوان الزامی است'); return; }
    setSaving(true); setError('');
    const body = JSON.stringify({ ...form, code: toEng(form.code), force });
    const j = modal === 'new'
      ? await API('/hazineh/create/', { method: 'POST', body })
      : await API(`/hazineh/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); load(); }
    else if (j.duplicate) { setModal('dup'); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/hazineh/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(); }
    else { setError(j.msg); setModal(j.used ? 'delete_blocked' : null); }
  };

  const Toggle = (opts, val, setVal) => (
    <div style={{ display: 'flex', gap: '4px' }}>
      {opts.map(([v, lbl]) => (
        <button key={v} onClick={() => setVal(v)}
          style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: '12px', fontWeight: '700',
            background: val === v ? '#1a2f50' : '#e8f0fe', color: val === v ? '#C8A84B' : '#1a2f50' }}>{lbl}</button>
      ))}
    </div>
  );

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFa(year)} onChange={handleYear} maxLength={4} placeholder="۱۴۰۵" style={ui.yearInput} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>نوع</label>
        {Toggle([['all', 'همه'], ['hazineh', 'هزینه‌ای'], ['tamalok', 'تملک']], kind, setKind)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>وضعیت</label>
        {Toggle([['all', 'همه'], ['active', 'فعال'], ['inactive', 'غیرفعال']], active, setActive)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سطح</label>
        <div style={{ display: 'flex', gap: '3px' }}>
          {[1, 2, 3, 4, 5, 6].map(n => (
            <button key={n} onClick={() => toggleLevel(n)}
              style={{ width: '26px', padding: '6px 0', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: '12px', fontWeight: '700',
                background: levels.includes(n) ? '#1a2f50' : '#e8f0fe', color: levels.includes(n) ? '#C8A84B' : '#1a2f50' }}>{toFa(n)}</button>
          ))}
        </div>
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
        style={{ ...ui.searchInput, flex: 1, minWidth: '140px' }} placeholder="جستجو (کد یا عنوان)..." />
      {loading && <span>⏳</span>}
      {!loading && data.length > 0 && <div style={ui.countBadge}>{toFa(data.length)} از {toFa(total)} مورد</div>}
    </>
  );

  const footer = (hasMore && !search) ? (
    <button onClick={loadNextPage} disabled={loadMore} style={{
      padding: '9px 36px', background: loadMore ? '#ccc' : 'linear-gradient(135deg,#1a2f50,#1a237e)',
      color: '#fff', border: 'none', borderRadius: '8px', cursor: loadMore ? 'default' : 'pointer',
      fontFamily: 'inherit', fontSize: '13px', fontWeight: '700' }}>
      {loadMore ? '⏳ در حال بارگذاری...' : `▼ ۲۰۰ مورد بعدی (${toFa(String(total - data.length))} باقی‌مانده)`}
    </button>
  ) : null;

  return (
    <>
      <ListPage user={user} onBack={onBack} title="کدهای هزینه" subtitle="بخش بودجه — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''} footer={footer}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {['کد سناما', 'کد هزینه', 'فرعی ۳', 'فرعی ۴', 'فرعی ۵', 'عنوان', 'نوع', 'سطح', 'موجودیت هزینه', 'فعال', 'اعتبار مصوب'].map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf6e3';
              const tc  = sel ? '#fff' : '#111';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, fontFamily: 'monospace', color: tc }}>{toFa(r.sanama_code)}</td>
                  <td style={{ ...ui.td, fontWeight: '700', fontFamily: 'monospace', color: sel ? '#C8A84B' : '#1a237e' }}>{toFa(r.code)}</td>
                  <td style={{ ...ui.td, color: tc }}>{r.code_ext ? toFa(r.code_ext) : '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{r.code_ext2 ? toFa(r.code_ext2) : '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{r.code_ext3 ? toFa(r.code_ext3) : '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '12px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.title}>{r.title}</td>
                  <td style={{ ...ui.td, fontSize: '10px', color: tc }}>{r.accounting_kind ? 'تملک' : 'هزینه‌ای'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.level)}</td>
                  <td style={{ ...ui.td, textAlign: 'right', fontSize: '10px', color: sel ? 'rgba(255,255,255,0.8)' : '#555', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.entity}>{r.entity || '•'}</td>
                  <td style={ui.td}>{r.is_active ? <span style={{ color: sel ? '#81c784' : '#1b5e20', fontWeight: '700' }}>✓</span> : ''}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#81c784' : r.credit_approved > 0 ? '#1b5e20' : '#ccc' }}>{fmtNum(r.credit_approved)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 داده‌ای یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ کد هزینه جدید' : `✎ ویرایش: ${selected?.title}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <Field label="کد هزینه" value={form.code} onChange={e => F('code', e.target.value)} half />
                <Field label="کد سناما" value={form.sanama_code} onChange={e => F('sanama_code', e.target.value)} type="number" half />
              </div>
              <div style={ui.row}>
                <Field label="کد فرعی سطح سه" value={form.code_ext} onChange={e => F('code_ext', e.target.value)} type="number" />
                <Field label="کد فرعی سطح چهار" value={form.code_ext2} onChange={e => F('code_ext2', e.target.value)} type="number" />
                <Field label="کد فرعی سطح پنج" value={form.code_ext3} onChange={e => F('code_ext3', e.target.value)} type="number" />
              </div>
              <div style={ui.row}>
                <Field label="عنوان" value={form.title} onChange={e => F('title', e.target.value)} />
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>نوع اعتبار</label>
                  <select value={form.accounting_kind} onChange={e => F('accounting_kind', Number(e.target.value))} style={ui.input}>
                    {KINDS.map((o, i) => <option key={i} value={i}>{o}</option>)}
                  </select>
                </div>
                <EntityField value={form.entity} onPick={r => setForm(f => ({ ...f, entity_id: r.id, entity: r.title }))} />
              </div>
              <div style={ui.divider} />
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#444', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => F('is_active', e.target.checked)} style={{ marginLeft: '8px' }} />
                فعال
              </label>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={() => save(false)} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'dup' && (
        <div style={ui.overlay} onClick={() => setModal('new')}>
          <div style={{ ...ui.modal, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#e65100,#f57c00)' }}>
              <div style={ui.mTitle}>⚠️ کد تکراری</div>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>
              کد هزینه تکراری است.<br />آیا از ثبت رکورد تکراری مطمئن هستید؟
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal('new')} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNOK') && <button onClick={() => save(true)} disabled={saving} style={{ ...ui.btnSave, background: '#e65100' }}>بله، ثبت شود</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '28px' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗑️</div>
              <div style={{ background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '10px 14px', fontWeight: '700', color: '#b71c1c' }}>
                [{toFa(selected?.code)}] {selected?.title}
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete_blocked' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#e65100,#f57c00)' }}>
              <div style={ui.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '28px', fontWeight: '700', color: '#1a2f50', lineHeight: '1.8' }}>
              این رکورد در جام ارجان مورد استفاده قرار گرفته<br />و قابل حذف نیست
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnSave}>باشه</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
